import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { authApi } from '../api/api';
import AuthBackground from '../components/AuthBackground';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Redirect if no email in state
  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  // Handle countdown for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleOtpChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split('').forEach((digit, index) => {
      if (index < 6) newOtp[index] = digit;
    });
    setOtp(newOtp);

    const lastIndex = Math.min(pastedData.length - 1, 5);
    const targetInput = document.getElementById(`otp-${lastIndex}`);
    targetInput?.focus();
  };

  const validatePassword = () => {
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (!/(?=.*[a-z])/.test(newPassword)) {
      setError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/(?=.*[A-Z])/.test(newPassword)) {
      setError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/(?=.*\d)/.test(newPassword)) {
      setError('Password must contain at least one number');
      return false;
    }
    if (!/(?=.*[@$!%*?&])/.test(newPassword)) {
      setError('Password must contain at least one special character (@$!%*?&)');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    if (!validatePassword()) {
      return;
    }

    setLoading(true);

    try {
      await authApi.resetPasswordWithOTP(email, otpCode, newPassword);
      setSuccess(true);
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to reset password. Please check your code and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    setError(null);

    try {
      await authApi.forgotPassword(email);
      setCountdown(60);
      setOtp(['', '', '', '', '', '']);
    } catch (err: any) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthBackground>
      {/* Card */}
      <div className="relative w-full max-w-[420px] mx-4 my-8">
        <div
          className="rounded-2xl"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.97)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Back button */}
          <div className="px-8 pt-6">
            <button
              onClick={() => navigate('/forgot-password')}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back
            </button>
          </div>

          {!success ? (
            <>
              {/* Header */}
              <div className="pt-4 pb-1 px-8 text-center">
                <div
                  className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
                  style={{
                    background: 'var(--action)',
                    boxShadow: '0 1px 3px color-mix(in srgb, var(--action) 30%, transparent)',
                  }}
                >
                  <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                  Reset Password
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  Enter the 6-digit code sent to <span className="font-medium text-primary">{email}</span>
                </p>
              </div>

              {/* Form */}
              <div className="px-8 pb-6 pt-5">
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* OTP Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Verification Code
                    </label>
                    <div className="flex gap-1.5 sm:gap-2 justify-center">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(e.target.value, index)}
                          onKeyDown={(e) => handleOtpKeyDown(e, index)}
                          onPaste={index === 0 ? handleOtpPaste : undefined}
                          className="w-11 h-12 text-center text-lg font-semibold bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:border-transparent focus:bg-white text-gray-900 transition-all duration-200"
                          disabled={loading}
                        />
                      ))}
                    </div>
                    <div className="mt-2 text-center">
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={resending || countdown > 0}
                        className="text-sm text-primary hover:underline disabled:text-gray-400 transition-colors font-medium"
                      >
                        {countdown > 0
                          ? `Resend code in ${countdown}s`
                          : resending
                          ? 'Sending...'
                          : 'Resend code'}
                      </button>
                    </div>
                  </div>

                  {/* New Password */}
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        id="newPassword"
                        type={showPassword ? 'text' : 'password'}
                        required
                        placeholder="Enter new password"
                        className="appearance-none block w-full px-4 py-2.5 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:border-transparent focus:bg-white transition-all duration-200"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        required
                        placeholder="Confirm new password"
                        className="appearance-none block w-full px-4 py-2.5 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:border-transparent focus:bg-white transition-all duration-200"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Password Requirements */}
                  <div className="text-xs text-gray-400 space-y-1 bg-gray-50 rounded-xl p-3">
                    <p className="font-medium text-gray-500">Password must contain:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-1">
                      <li>At least 8 characters</li>
                      <li>One uppercase & one lowercase letter</li>
                      <li>One number</li>
                      <li>One special character (@$!%*?&)</li>
                    </ul>
                  </div>

                  {error && (
                    <div role="alert" className="flex items-start gap-2 p-3 text-sm bg-red-50 border border-red-200 text-red-700 rounded-xl">
                      <svg className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd"/>
                      </svg>
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !otp.every(d => d) || !newPassword || !confirmPassword}
                    // Colour in classes, not the style object — see ForgotPassword.
                    // This CTA starts disabled until the OTP and both passwords
                    // are filled, so the disabled state must stay legible.
                    className="w-full py-3 px-4 rounded-xl font-semibold text-sm text-center flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--focus-ring)] bg-[color:var(--action)] text-[color:var(--action-text)] hover:bg-[color:var(--action-hover)] disabled:bg-[color:var(--bg-sunken)] disabled:text-[color:var(--text-disabled)] disabled:cursor-not-allowed"
                    style={{
                      // Cleared whenever disabled, not just while loading — see
                      // ForgotPassword. This CTA starts disabled.
                      boxShadow: (loading || !otp.every(d => d) || !newPassword || !confirmPassword)
                        ? 'none'
                        : '0 1px 3px color-mix(in srgb, var(--action) 30%, transparent)',
                    }}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        Resetting...
                      </span>
                    ) : 'Reset Password'}
                  </button>
                </form>
              </div>
            </>
          ) : (
            <>
              {/* Success state */}
              <div className="px-8 pb-6 pt-4 text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mb-2">Password Reset!</h2>
                <p className="text-sm text-gray-500 mb-6">
                  Your password has been successfully reset. You can now sign in with your new password.
                </p>

                <button
                  onClick={() => navigate('/login')}
                  className="w-full py-3 px-4 rounded-xl font-semibold text-sm text-white text-center flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--focus-ring)]"
                  style={{
                    background: 'var(--action)',
                    boxShadow: '0 1px 3px color-mix(in srgb, var(--action) 30%, transparent)',
                  }}
                >
                  Go to Login
                </button>
              </div>
            </>
          )}

          {/* Footer */}
          <div className="px-8 pb-6">
            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400">
              <span>Powered by</span>
              <img src="/favicon-64.png" alt="Nextserve" className="h-4 w-4 rounded-sm" />
              <span className="font-medium text-gray-500">Nextserve</span>
            </div>
          </div>
        </div>
      </div>
    </AuthBackground>
  );
};

export default ResetPassword;
